import { Controller, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Select, Text, TextField } from "@aprosoja/ui";

import { useSignUpContext } from "@/app/screens/sign-up/context/sign-up.context";
import { addressStepFormSchema, AddressStepFormValues } from "./schema";
import { statesOptions } from "./static";
import ControlButtons from "../components/control-buttons";
import ContentWrapper from "../components/content-wrapper";
import useAddressStep from "./use-address";

const AddressStep = () => {
  const { signUpReducer } = useSignUpContext();
  const formMethods = useForm<AddressStepFormValues>({
    resolver: zodResolver(addressStepFormSchema),
    defaultValues: signUpReducer?.userAddress
      ? { address: signUpReducer.userAddress }
      : { address: { country: "Brasil" } },
  });
  const { onSubmit } = useAddressStep();

  return (
    <form
      onSubmit={formMethods.handleSubmit(onSubmit)}
      className="flex flex-col w-full gap-size-x8"
    >
      <ContentWrapper
        title="Endereço de cobrança"
        description="O endereço de cobrança deve ser um endereço nacional válido."
      >
        <div className="flex w-full gap-size-x4">
          <div className="w-[320px]">
            <Controller
              control={formMethods.control}
              name="address.postalCode"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="CEP"
                  mask="cep"
                  placeholder="00.000-000"
                  required
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <Controller
            control={formMethods.control}
            name="address.country"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="País de Residência"
                required
                errorMessage={error?.message}
                disabled
              />
            )}
          />
          <Controller
            control={formMethods.control}
            name="address.stateCode"
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                onValueChange={(value) => field.onChange(value)}
                options={statesOptions}
                label="Estado"
                placeholder="Rio Grande do Sul"
                required
                errorMessage={error?.message}
              />
            )}
          />
        </div>
        <div className="flex w-full gap-size-x4">
          <Controller
            control={formMethods.control}
            name="address.city"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Cidade / Município"
                placeholder="Passo Fundo"
                required
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={formMethods.control}
            name="address.neighbourhood"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Bairro"
                required
                placeholder="Zona rural"
                errorMessage={error?.message}
              />
            )}
          />
        </div>
        <div className="flex w-full gap-size-x4">
          <Controller
            control={formMethods.control}
            name="address.street"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Endereço"
                placeholder="BR-116"
                required
                errorMessage={error?.message}
              />
            )}
          />
          <div className="w-[160px]">
            <Controller
              control={formMethods.control}
              name="address.number"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Número"
                  placeholder="S/N"
                  required
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <div className="w-[320px]">
            <Controller
              control={formMethods.control}
              name="address.complement"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Complemento"
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
        </div>
      </ContentWrapper>
      <ControlButtons isLoading={formMethods.formState.isSubmitting} />
    </form>
  );
};

export default AddressStep;
